import { get, isObject, isPlainObject, set } from 'lodash';

// AER-1701: the app's value contract for saving and reading records, in one
// dependency-free module (utils/index.ts re-exports these — import from either).
// One meaning per value, enforced at one boundary:
//
//   undefined      -> "not supplied": stripped client-side by removeEmpty(). An absent
//                     key never overrides the server-side merge
//                     ({ ...storedRecord, ...payload }), so the stored value is kept.
//   null / ''      -> "clear this field": KEPT in the payload so it survives the wire,
//                     wins the merge, and is then translated to attribute-removal by
//                     toDynamoItem() at the single DynamoDB write boundary
//                     (createItem() in server/src/utils/db.ts).
//   [] / 0 / false -> real values: they travel and are stored as-is. A stored [] is how
//                     "explicitly none" persists (AER-1599 — a dropped array key used to
//                     be defaulted back on by _User, silently re-enabling push alerts).
//
// History (AER-1702/1703): removeEmpty() used to drop ''/[] keys and encode null as
// the string 'null' before every request, because pre-May-2020 DynamoDB rejected empty
// strings anywhere. Client-side stripping is what made cleared fields silently
// resurrect through the server merge (AER-1635, AER-1702), and the sentinel is what
// put the literal string "null" into storage (AER-1703). DynamoDB constraints are now
// handled exclusively by toDynamoItem(); legacy 'null' sentinels already in the table
// stay readable forever via fixEmpty() and are shed lazily the next time their record
// is saved. Nothing may re-strip '' / null / [] between the client and the PUT.
// tslint:disable-next-line:no-any
export const removeEmpty = (obj: any) => {
  for (let key in obj) {
    const o = obj[key];
    if (o === undefined) {
      delete obj[key];
    } else if (Array.isArray(o)) {
      o.forEach(oo => {
        if (isObject(oo)) {
          removeEmpty(oo);
        }
      });
    } else if (isObject(o)) {
      removeEmpty(o);
    }
  }
  return obj;
};

// AER-1701: THE write-side normalizer for DynamoDB items — the inverse of fixEmpty().
// Runs on the COMPLETE record (after any merge, right before the PUT in createItem()),
// so deleting a key here removes the attribute for real. Pure: returns a new object.
//
//  - null / '' / undefined keys are removed at every depth: "cleared" is represented
//    by attribute absence. Not just tidiness — equipmentSN / dentalOrgId / locationId /
//    serviceOrgId are GSI range keys (pk-*-index), and DynamoDB still rejects the whole
//    write if a key attribute holds '' or a non-string type such as native NULL (the
//    2020 empty-value change covers non-key attributes only). Absence simply drops the
//    item from the sparse index; nothing queries the legacy 'null' rows — every GSI
//    lookup passes a real id.
//  - the legacy 'null' / 'NULL' string sentinels are treated as cleared too, so
//    payloads from not-yet-updated clients keep working, and any record still carrying
//    a sentinel sheds it the next time it is saved (lazy self-heal; fixEmpty() covers
//    rows that are never re-saved). The string "null" as literal user data was already
//    unrepresentable — fixEmpty() has always decoded it on read.
//  - [] and {} are stored as real empty values (legal in DynamoDB; a stored [] is
//    load-bearing for notification opt-outs — see _shared/interfaces/user.ts).
//  - null ELEMENTS inside arrays are kept as native NULLs: positions matter, and the
//    [null] "explicitly off" sentinel must round-trip. fixEmpty() maps legacy 'null'
//    strings in that position back to null, so both generations read identically.
//  - 0 and false are real values and always kept. Non-plain objects (class instances,
//    Dates) pass through untouched, exactly as the DocumentClient received them before
//    this function existed.
//
// AER-1794: `keepNull` is for device telemetry (publish rows and the equipment status
// record that caches them), where null means "the device could not read this value",
// not "clear this field". There a null, or a legacy 'null' sentinel, is stored as a
// native NULL, so a stored reading comes back as null exactly as it did before
// AER-1701 (when it was written as 'null' and decoded by fixEmpty()). Legal because
// those items carry none of the GSI key attributes. '' and undefined are still removed.
// tslint:disable-next-line:no-any
export const toDynamoItem = (
  item: any,
  opts: { keepNull?: boolean } = {}
): any => {
  // tslint:disable-next-line:no-any
  const isNullish = (v: any) => v === null || v === 'null' || v === 'NULL';
  // tslint:disable-next-line:no-any
  const isCleared = (v: any) =>
    v === undefined || v === '' || (!opts.keepNull && isNullish(v));

  // tslint:disable-next-line:no-any
  const walk = (v: any): any => {
    if (Array.isArray(v)) {
      return v.map(el => (el === 'null' || el === 'NULL' ? null : walk(el)));
    }
    if (isPlainObject(v)) {
      // tslint:disable-next-line:no-any
      const out: any = {};
      for (let key in v) {
        if (!isCleared(v[key])) {
          out[key] = isNullish(v[key]) ? null : walk(v[key]);
        }
      }
      return out;
    }
    return v;
  };

  return isPlainObject(item) ? walk(item) : item;
};

// tslint:disable-next-line:no-any
export const fixEmpty = (obj: any) => {
  if (!isObject(obj)) {
    return obj;
  }

  for (let key in obj) {
    let o = get(obj, key);
    if (o === 'null' || o === 'NULL') {
      set(obj, key, null);
    } else if (Array.isArray(o)) {
      set(
        obj,
        key,
        o.map(oo => {
          if (oo === 'null' || oo === 'NULL') {
            return null;
          } else if (isObject(oo)) {
            // AER-1703: an object nested inside an array was the one position this
            // decode never reached, so a 'null' stored there stayed a raw string
            // (e.g. equipment.previousSerials[].deviceId). The encode/decode pair is
            // now symmetric at every depth.
            return fixEmpty(oo);
          } else {
            return oo;
          }
        })
      );
    } else if (isObject(o)) {
      o = fixEmpty(o);
    }
  }
  return obj;
};
